import * as _ from 'lodash';
import { Config } from '../../../helper';
import { getFieldValue, safeHref } from '../helper';

const Provider = {
  unknown: 'unknown',
  email: 'email',
  linkedin: 'linkedin',
  github: 'github',
  twitterX: 'x-twitter', // brand: X (formerly Twitter); matches social-icons/x-twitter.svg
  googleScholar: 'google-scholar',
};

function detectProvider(url) {
  const {protocol, hostname} = url;

  if (protocol === 'mailto:') {
    return Provider.email;
  }

  const cleanHostname = hostname.replace(/^www\./, '').toLowerCase();

  // Match the exact domain or a proper subdomain only, so look-alikes like
  // "evillinkedin.com" don't get a trusted provider icon.
  const matchesDomain = (host, domain) => host === domain || host.endsWith(`.${domain}`);

  if (matchesDomain(cleanHostname, 'scholar.google.com')) {
    return Provider.googleScholar;
  }

  if (matchesDomain(cleanHostname, 'linkedin.com')) {
    return Provider.linkedin;
  }

  if (matchesDomain(cleanHostname, 'x.com') || matchesDomain(cleanHostname, 'twitter.com')) {
    return Provider.twitterX;
  }

  if (matchesDomain(cleanHostname, 'github.com') || matchesDomain(cleanHostname, 'github.io')) {
    return Provider.github;
  }

  return Provider.unknown;
}

// Accessible name for the icon-only link, so screen readers don't announce a bare URL.
const PROVIDER_LABELS = {
  [Provider.email]: 'Email',
  [Provider.linkedin]: 'LinkedIn',
  [Provider.github]: 'GitHub',
  [Provider.twitterX]: 'X',
  [Provider.googleScholar]: 'Google Scholar',
};

function providerLabel(provider, url) {
  // Fall back to the host for unrecognized providers — more informative than a generic "Link".
  return PROVIDER_LABELS[provider] || url.hostname.replace(/^www\./, '');
}

function convertToLinkObject(value) {
  // safeHref validates the protocol (http/https/mailto only) and normalizes the URL,
  // so javascript:/data: links never reach the rendered href. It already returned a
  // parseable absolute URL, so new URL() below cannot throw.
  const href = safeHref(value);
  if (!href) return null;

  const url = new URL(href);
  const provider = detectProvider(url);
  return {provider, url: href, label: providerLabel(provider, url)};
}

function splitLines(input) {
  return typeof input === 'string' ? input.split('\n').map(s => s.trim()).filter(Boolean) : [];
}

export function buildLinks(source, mapping) {
  const raw = getFieldValue(source, mapping, Config.Mapping.ProfileTeaser.Links, []);
  if (!Array.isArray(raw)) return [];

  const links = [];
  raw.forEach(entry => {
    const link = convertToLinkObject(entry);
    if (link) links.push(link);
  });

  return links;
}

export function buildExtraFields(source, mapping) {
  const fieldNames = splitLines(mapping?.[Config.Mapping.ProfileTeaser.ExtraFields]);
  const labels = splitLines(mapping?.[Config.Mapping.ProfileTeaser.ExtraFieldLabels]);

  const extraFields = [];
  fieldNames.forEach((fieldName, index) => {
    const raw = _.get(source, fieldName);
    let value;
    if (Array.isArray(raw)) {
      value = raw.filter(Boolean).join(', ');
    } else if (raw === undefined || raw === null || typeof raw === 'object') {
      value = '';
    } else {
      value = String(raw);
    }
    if (value === '') return;
    extraFields.push({key: fieldName, label: labels[index] || fieldName, value});
  });

  return extraFields;
}
